import { Component, PropsWithChildren } from 'react';
import posed from 'react-pose';
import { Icon } from '../Icon';
import { createId } from '../utils/IdGenerator';
import textContent from '../utils/textContent';

import { faTimes } from '@fortawesome/pro-light-svg-icons';

import { faCheckCircle, faExclamationTriangle, faInfoSquare, faTimesOctagon } from '@fortawesome/pro-solid-svg-icons';

import React from 'react';
import './Notification.scss';

import { INotificationProps } from './types';

/**
 * To be used to provide feedback to user actions, taking "success", "error", "info", and "progress" type
 * props to determine the component's styles. Can also be used to surface messages regarding background processes,
 * eg "Your upload has finished processing." For calling out static, on-page information see Alerts.
 */

export default class Notification extends Component<PropsWithChildren<INotificationProps>> {
  public static defaultProps = {
    openTimeLength: 3000,
  };

  private PosedContainer: any;
  private dismissalTimer: number;
  private id: string;

  constructor(props: INotificationProps) {
    super(props);
    this.id = createId('notification');
    this.PosedContainer = posed.div({
      close: {
        applyAtEnd: { display: 'none' },
        opacity: 0,
        transition: {
          duration: 200,
          ease: 'easeIn',
        },
        x: '-50%',
        y: '-100%',
      },
      open: {
        applyAtStart: { display: 'flex' },
        onPoseComplete: () => {
          this.autoDismiss();
        },
        opacity: 1,
        transition: {
          duration: 200,
          ease: 'easeOut',
        },
        x: '-50%',
        y: 0,
      },
    });
  }

  public autoDismiss = () => {
    const { onHide, openTimeLength } = this.props;
    if (openTimeLength === 'user-closes') {
      return;
    }

    // Ensure that we don't double up timers
    clearTimeout(this.dismissalTimer);
    this.dismissalTimer = window.setTimeout(onHide, openTimeLength);
  };

  /**
   * Stop the dismissal timer on mouseEnter -- the user is probably trying to
   * read or interact with the notification
   */
  public stopDismissal = () => {
    clearTimeout(this.dismissalTimer);
  };

  public getNotificationTypeIcon = () => {
    const { type } = this.props;
    switch (type) {
      case 'success':
        return <Icon icon={faCheckCircle} size="lg" />;
      case 'info':
        return <Icon icon={faInfoSquare} size="lg" />;
      case 'inprogress':
        return (
          <div className="spinner-container">
            <div className="spinner" />
          </div>
        );
      case 'error':
        return <Icon icon={faTimesOctagon} size="lg" />;
      case 'warning':
        return <Icon icon={faExclamationTriangle} size="lg" />;
      default:
        return <Icon icon={faCheckCircle} size="lg" />;
    }
  };

  public render() {
    const { message, type } = this.props;
    const { PosedContainer } = this;

    return (
      <PosedContainer
        className="notification-container"
        style={{ top: 0, left: '50%' }}
        aria-hidden={!this.props.visible}
        pose={this.props.visible ? 'open' : 'close'}
        key={this.id}
      >
        <div
          className={`notification notification-${type}`}
          title={this.getTitle()}
          onMouseEnter={this.stopDismissal}
          onMouseLeave={this.autoDismiss}
          role="alert"
        >
          <span className={`type-icon ${type}`}>{this.getNotificationTypeIcon()}</span>
          <span className="notification-message">{message}</span>
          <span
            className="close-icon"
            title="Close notification"
            data-testid="hlc-notification-close-icon"
            onClick={this.props.onHide}
          >
            <Icon className="notification-close-icon" icon={faTimes} size="lg" />
          </span>
        </div>
      </PosedContainer>
    );
  }

  /**
   * Return a title string
   */
  private getTitle(): string {
    const { message, title } = this.props;

    if (title) {
      return title;
    }

    if (typeof message === 'string') {
      return message.trim();
    } else {
      // If message is an element, we need to grab its textContent
      return textContent(message).trim() || '';
    }
  }
}
